import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import moment from 'moment';
import classnames from 'classnames';

import { getFormValues } from 'redux-form';

import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';

import Progress from 'components/common/Progress';
import Dialog, {
  DialogActions,
  DialogContent,
  DialogTitle,
} from 'components/common/Dialog';
import Button from 'components/common/Button';
import IconUpdate from 'components/icons/IconUpdate';

import { fetchInventory } from 'redux/modules/inventory';
import {
  fetchInventoryStockLineHistory,
  clearInventoryStockLineHistory,
} from 'redux/modules/inventoryStockLineHistory';

import throttle from 'utils/throttle';
import formatUtcToLocalDate from 'utils/formatUtcToLocalDate';

import timeDateFormat from 'config/timeDateFormat';

import InventoryCreateEditTable from 'components/inventory/InventoryCreateEditDialog/InventoryCreateEditTable';

import InventoryStockLineHistoryViewFilter from './InventoryStockLineHistoryViewFilter';
import InventoryStockLineHistoryViewTable from './InventoryStockLineHistoryViewTable';

const styles = theme => ({
  paper: {
    maxWidth: 1200,
    minWidth: 1200,
  },
  gridInfo: {
    paddingLeft: 24,
    paddingRight: 24,
  },
  gridItemTotal: {
    display: 'flex',
  },
  filter: {
    marginTop: 16,
    paddingLeft: 24,
    paddingRight: 24,
    marginBottom: 16,
  },
  action: {
    '&:first-child': {
      position: 'absolute',
      left: 14,
      top: 8,
    },
  },
  typographyDate: {
    position: 'absolute',
    left: '50%',
    top: 'auto',
    transform: 'translateX(-50%)',
    '&:first-child': {
      left: '50%',
      top: 'auto',
      transform: 'translateX(-50%)',
    },
  },
  table: {
    padding: '16px 24px',
    borderBottom: `1px solid ${theme.palette.primary.A100}`,
  },
  typographyLowInventory: {
    color: theme.palette.notification[500],
  },
  typographyOutOfStock: {
    color: theme.palette.validation[500],
  },
  iconUpdate: {
    margin: '-2px 4px 0 0',
    verticalAlign: 'middle',
  },
});

class InventoryStockLineHistoryViewDialog extends Component {
  constructor(props) {
    super(props);

    this.state = {
      date: null,
      triggerLoading: true,
    };

    this.onRequest = this.onRequest.bind(this);
    this.onDialogEnter = this.onDialogEnter.bind(this);
    this.onRefreshButtonClick = this.onRefreshButtonClick.bind(this);

    this.onRequest = throttle(this.onRequest);
  }

  componentWillReceiveProps(nextProps) {
    const { open, count, loading } = this.props;
    const newState = {};

    if (count === 0 || !open) {
      newState.triggerLoading = true;
    } else {
      newState.triggerLoading = false;
    }
    if (open && nextProps.loading < loading) {
      newState.triggerLoading = false;
    }
    this.setState(newState);
  }

  componentDidUpdate(prevProps, prevState) {
    const { open, filters } = this.props;
    const { date } = this.state;
    const dateChanged = prevState.date < date;
    const prevLineType = prevProps.filters['Line Type']
      ? prevProps.filters['Line Type'].join('')
      : null;
    const currentLineType = filters['Line Type']
      ? filters['Line Type'].join('')
      : null;
    const filtersChanged = prevLineType !== currentLineType;
    if (open && (dateChanged || filtersChanged)) {
      this.onRequest();
    }
  }

  onRequest() {
    const { actions, filters } = this.props;
    const { triggerLoading } = this.state;
    const requestFilters = filters && Object.keys(filters).length > 0
      ? Object.keys(filters).map((key) => {
        const name = key;
        let operation;
        let caseSensitivity = null;
        let val = filters[key];
        switch (key) {
          case 'Line Type':
            operation = 'in';
            if (val.indexOf('All') !== -1) {
              val = ['Incoming', 'Sale', 'Returns', 'Clear', 'Initial'];
            }
            break;
          case 'Product ID':
            operation = 'eq';
            break;
          default:
            operation = 'startswith';
            caseSensitivity = false;
        }
        return {
          name,
          operation,
          val,
          ...caseSensitivity !== null ? { case_sensitivity: caseSensitivity } : {},
        };
      })
      : null;
    actions.fetchInventoryStockLineHistory({
      filters: requestFilters,
      triggerLoading,
    });
  }

  onDialogEnter() {
    const { actions, Product } = this.props;
    actions.clearInventoryStockLineHistory();
    if (!Product.Image) {
      actions.fetchInventory({
        ID: Product.ID,
      });
    }
    this.setState({
      date: moment().utc(),
      triggerLoading: true,
    });
  }

  onRefreshButtonClick() {
    this.props.actions.clearInventoryStockLineHistory();
    if (this.scrollbarsRef) {
      this.scrollbarsRef.scrollTop(0);
    }
    this.setState({
      date: moment().utc(),
      triggerLoading: true,
    });
  }

  render() {
    const {
      open,
      loading,
      onClose,
      inventoryStockLineHistory,
      onStockLineEdit,
      business,
      Product,
      filters,
      fromInventory,
      classes,
    } = this.props;
    const { date } = this.state;
    const totalQuantity = inventoryStockLineHistory
      .reduce((accumulator, currentValue) => {
        if (currentValue['Line Type'] === 'Sale') {
          return accumulator - currentValue.Quantity;
        }
        return accumulator + currentValue.Quantity;
      }, 0);
    return (
      <Dialog
        open={open}
        onClose={onClose}
        onEnter={this.onDialogEnter}
        maxWidth="md"
        classes={{
          paper: classes.paper,
        }}
      >
        <DialogTitle onCloseClick={onClose}>
          <Typography
            variant="title"
            color="secondary"
            align="center"
          >
            {Product['Product Name']
              ? `${Product['Product Name']}: Activity Log`
              : 'Activity Log'}
          </Typography>
        </DialogTitle>
        <DialogContent
          withScrollbars
          disableGutters
          fullHeight
          scrollbarsRef={(scrollbarsRef) => {
            this.scrollbarsRef = scrollbarsRef;
          }}
        >
          {(Product.ID && Product.Image) &&
            <InventoryCreateEditTable
              className={classes.table}
              {...Product}
            />
          }
          {(loading > 0) ? (
            <Progress />
          ) : (
            <Fragment>
              <Grid
                className={classes.gridInfo}
                container
                spacing={0}
                justify="space-between"
                alignItems="center"
              >
                <Grid item>
                  <InventoryStockLineHistoryViewFilter
                    className={classes.filter}
                    form="inventoryStockLineHistoryFilter"
                    title="Type Filters"
                  />
                </Grid>
                <Grid
                  className={classes.gridItemTotal}
                  item
                >
                  <Typography color="secondary">
                    {(filters['Line Type'][0] === 'All' || filters['Line Type'].length === 3)
                      ? 'Remaining in stock:'
                      : 'Filtered quantity:'}
                  </Typography>
                  {'\u00A0'}
                  <Typography
                    className={classnames({
                      [classes.typographyLowInventory]: totalQuantity <= 5 && totalQuantity > 0,
                      [classes.typographyOutOfStock]: totalQuantity <= 0,
                    })}
                  >
                    {totalQuantity}
                  </Typography>
                </Grid>
              </Grid>
              <InventoryStockLineHistoryViewTable
                inventoryStockLineHistory={inventoryStockLineHistory}
                onStockLineEdit={onStockLineEdit}
                {...{ 'Product ID': Product.ID }}
                {...{ 'Calories Per Unit': Product['Calories Per Unit'] }}
                business={business}
              />
            </Fragment>
          )}
        </DialogContent>
        <DialogActions
          classes={{
            action: classes.action,
          }}
        >
          {!fromInventory &&
            <Button
              href={`/inventory/products?ID=${Product.ID}`}
              target="_blank"
              color="primary"
              underline
            >
              Open in Inventory
            </Button>
          }
          {date &&
            <Typography
              className={classes.typographyDate}
              color="secondary"
            >
              <IconUpdate className={classes.iconUpdate} />
              Last updated {formatUtcToLocalDate(
                date,
                timeDateFormat,
                timeDateFormat,
                business['Company Address']['Time Zone'],
                business['Company Address']['Observes Daylight Saving'],
              )}
            </Typography>
          }
          <Button
            onClick={this.onRefreshButtonClick}
            variant="raised"
          >
            Refresh
          </Button>
          <Button
            onClick={onClose}
            variant="raised"
            color="primary"
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

InventoryStockLineHistoryViewDialog.propTypes = {
  Product: PropTypes.shape({
    ID: PropTypes.number,
    'Product Name': PropTypes.string,
  }),
  inventoryStockLineHistory: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number.isRequired,
  })).isRequired,
  count: PropTypes.number.isRequired,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  loading: PropTypes.number.isRequired,
  actions: PropTypes.shape({
    fetchInventory: PropTypes.func.isRequired,
    fetchInventoryStockLineHistory: PropTypes.func.isRequired,
    clearInventoryStockLineHistory: PropTypes.func.isRequired,
  }).isRequired,
  filters: PropTypes.shape({
    'Line Type': PropTypes.arrayOf(PropTypes.string),
  }),
  onStockLineEdit: PropTypes.func,
  business: PropTypes.shape({
    ID: PropTypes.number,
  }),
  loadProduct: PropTypes.bool,
  fromInventory: PropTypes.bool,
  classes: PropTypes.shape({
    gridInfo: PropTypes.string.isRequired,
    gridItemTotal: PropTypes.string.isRequired,
    filter: PropTypes.string.isRequired,
    action: PropTypes.string.isRequired,
    typographyLowInventory: PropTypes.string.isRequired,
    typographyOutOfStock: PropTypes.string.isRequired,
  }).isRequired,
};

InventoryStockLineHistoryViewDialog.defaultProps = {
  open: false,
  onClose: () => {},
  filters: {},
  onStockLineEdit: () => {},
  business: {},
  Product: {},
  loadProduct: false,
  fromInventory: false,
};

const mapStateToProps = (store, ownProps) => {
  const inventoryStockLineHistoryFilterValues = getFormValues('inventoryStockLineHistoryFilter')(store);
  return {
    inventoryStockLineHistory: store.inventoryStockLineHistory.data,
    count: store.inventoryStockLineHistory.count,
    loading: store.inventoryStockLineHistory.loading
      + store.inventory.loading,
    filters: {
      ...ownProps.Product && ownProps.Product.ID ?
        { 'Product ID': ownProps.Product.ID } : {},
      ...inventoryStockLineHistoryFilterValues
        && Object.keys(inventoryStockLineHistoryFilterValues)
          .filter(item => inventoryStockLineHistoryFilterValues[item]).length > 0
        ? {
          'Line Type': Object.keys(inventoryStockLineHistoryFilterValues)
            .filter(item => inventoryStockLineHistoryFilterValues[item]),
        } : {
          'Line Type': ['All'],
        },
    },
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    fetchInventory,
    fetchInventoryStockLineHistory,
    clearInventoryStockLineHistory,
  }, dispatch),
});


export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles)(InventoryStockLineHistoryViewDialog),
);



// WEBPACK FOOTER //
// ./src/components/inventory/InventoryStockLineHistoryViewDialog/InventoryStockLineHistoryViewDialog.js